/* eslint-disable no-undef */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Loader2, Send, Sparkles, Trash2 } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import "./AIChat.scss";
import { AiAssistant } from "../../../api";
import { ChatMessage } from "../../../components/Student/ChatMessage";
import { useAuth } from "../../../hooks";

const STORAGE_KEY = "edunova-ai-chat-history-v2";
const STORAGE_VERSION = "gemini-v1";
const MAX_SAVED_MESSAGES = 40;
const SUGGESTIONS = [
  "Dame consejos para organizar mis estudios en Edunova.",
  "¿Cómo puedo mejorar mi promedio este ciclo académico?",
];

export const AIChat = () => {
  const { accessToken, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [usage, setUsage] = useState(null);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesRef = useRef([]);
  const hasHydrated = useRef(false);

  const assistantController = useMemo(() => new AiAssistant(), []);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (!hasHydrated.current || typeof window === "undefined") return;
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(messages.slice(-MAX_SAVED_MESSAGES))
    );
  }, [messages]);

  useEffect(() => {
    if (typeof window === "undefined") {
      hasHydrated.current = true;
      return;
    }
    
    // Check version and migrate if needed
    const currentVersion = window.localStorage.getItem("edunova-ai-chat-version");
    if (currentVersion !== STORAGE_VERSION) {
      // Clear old format messages
      window.localStorage.removeItem("edunova-ai-chat-history");
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.setItem("edunova-ai-chat-version", STORAGE_VERSION);
      hasHydrated.current = true;
      return;
    }
    
    const prevMessages = window.localStorage.getItem(STORAGE_KEY);
    if (prevMessages) {
      try {
        const parsed = JSON.parse(prevMessages);
        if (Array.isArray(parsed) && parsed.length) {
          setMessages(parsed);
        }
      } catch (storageError) {
        console.warn("No se pudo restaurar el historial del chat", storageError);
      }
    }
    hasHydrated.current = true;
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const buildGeminiPayload = useCallback(
    (userMessage) => {
      return {
        userMessage: userMessage.content,
        tasks: [], // Can be enriched in the future
        classes: [], // Can be enriched in the future
        exams: [], // Can be enriched in the future
        progress: {}, // Can be enriched in the future
        preferences: {}, // Can be enriched in the future
        instructions: "", // Optional instructions
        metadata: {
          app: "edunova-frontend",
          studentId: user?.id || null,
          studentEmail: user?.email || null,
          timestamp: new Date().toISOString(),
        },
      };
    },
    [user]
  );

  const resolveAssistantMessage = useCallback((response) => {
    // Gemini backend returns: { message, meta }
    const content = response?.message || 
                   response?.data?.message ||
                   "Lo siento, no pude generar una respuesta en este momento.";

    const usageInfo = response?.meta?.usage || response?.usage || null;

    return {
      id: uuidv4(),
      role: "assistant",
      content,
      createdAt: new Date().toISOString(),
      usage: usageInfo,
      // Store metadata for reference
      metadata: {
        model: response?.meta?.model || null,
        latencyMs: response?.meta?.latencyMs || null,
        requestId: response?.meta?.requestId || null,
      },
    };
  }, []);

  const sendMessage = useCallback(
    async (customMessage) => {
      const baseMessage =
        typeof customMessage === "string" ? customMessage : inputValue;
      const trimmedMessage = baseMessage.trim();
      if (!trimmedMessage || isLoading) return;

      const userMessage = {
        id: uuidv4(),
        role: "user",
        content: trimmedMessage,
        createdAt: new Date().toISOString(),
      };

      setMessages((prev) => [
        ...prev,
        userMessage,
        {
          ...userMessage,
          role: "assistant",
          id: `${userMessage.id}-placeholder`,
          content: "",
          isLoading: true,
        },
      ]);
      setInputValue("");
    setError("");
    setIsLoading(true);

    try {
      const payload = buildGeminiPayload(userMessage);
      const response = await assistantController.sendMessage(
        accessToken,
        payload
      );

      const assistantMessage = resolveAssistantMessage(response);
      setUsage(assistantMessage?.usage || null);

      setMessages((prev) =>
        prev
          .filter((message) => message.id !== `${userMessage.id}-placeholder`)
          .concat(assistantMessage)
      );
    } catch (requestError) {
      console.error("Error al consultar al asistente", requestError);
      setMessages((prev) =>
        prev.filter((message) => message.id !== `${userMessage.id}-placeholder`)
      );
      
      // Handle specific error codes
      let errorMessage = "Tuvimos un inconveniente con el asistente. Intenta nuevamente.";
      let assistantResponse = "Lo siento, no pude generar una respuesta en este momento.";
      
      if (requestError?.status === 400) {
        errorMessage = "Solicitud inválida. Por favor, verifica tu mensaje e intenta nuevamente.";
        assistantResponse = "Tu mensaje no pudo ser procesado. Por favor, reformúlalo e intenta de nuevo.";
      } else if (requestError?.status === 429) {
        errorMessage = "Límite de consultas alcanzado. Por favor, espera un momento antes de enviar otro mensaje.";
        assistantResponse = "Has alcanzado el límite de mensajes. Por favor, espera un momento antes de continuar.";
      } else if (requestError?.status === 502 || requestError?.status === 503) {
        errorMessage = "El servicio de IA está temporalmente no disponible. Intenta nuevamente en unos momentos.";
        assistantResponse = "El servicio está temporalmente no disponible. Por favor, inténtalo otra vez en unos momentos.";
      } else if (requestError?.message) {
        errorMessage = requestError.message;
      }
      
      setError(errorMessage);
      setUsage(null);
      setMessages((prev) => [
        ...prev,
        {
          id: uuidv4(),
          role: "assistant",
          content: assistantResponse,
          createdAt: new Date().toISOString(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
    }, [
      accessToken,
      assistantController,
      buildGeminiPayload,
      inputValue,
      isLoading,
      resolveAssistantMessage,
    ]);

  const handleSubmit = useCallback(
    (event) => {
      event?.preventDefault();
      sendMessage();
    },
    [sendMessage]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  const handleSuggestionClick = useCallback(
    (suggestion) => {
      if (isLoading) return;
      setInputValue(suggestion);
      inputRef.current?.focus();
      sendMessage(suggestion);
    },
    [isLoading, sendMessage]
  );

  const handleClearConversation = () => {
    setMessages([]);
    setUsage(null);
    setError("");
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.setItem("edunova-ai-chat-version", STORAGE_VERSION);
    }
  };

  const hasConversation = messages.some((message) => message.role);

  return (
    <div className="ai-chat-page">
      <div className="ai-chat-container">
        <header className="ai-chat-header">
          <div className="ai-chat-header__title">
            <Sparkles className="ai-chat-header__icon" />
            <h1>Edunova AI Assistant</h1>
          </div>
          <p className="ai-chat-header__subtitle">
            Resuelve tus dudas académicas, organiza tu agenda y recibe tips
            personalizados con la asistencia de Gemini Flash.
          </p>
          <button
            type="button"
            className="ai-chat-header__clear-btn"
            onClick={handleClearConversation}
            disabled={!hasConversation && !inputValue}
          >
            <Trash2 size={16} /> Limpiar conversación
          </button>
        </header>

        <main className="ai-chat-messages">
          {!hasConversation && !isLoading ? (
            <div className="ai-chat-welcome">
              <Sparkles className="ai-chat-welcome__icon" />
              <h2>Hola{user?.firstName ? `, ${user.firstName}` : ""} 👋</h2>
              <p>
                Estoy aquí para ayudarte con tus clases, pagos, avisos y con
                todo lo que necesites para tener un gran ciclo en Edunova.
              </p>
              <ul className="ai-chat-welcome__features">
                <li>Resumen de tareas, clases y exámenes</li>
                <li>Explicaciones simples con pasos accionables</li>
                <li>Sugerencias personalizadas según tu avance</li>
              </ul>
              <p className="ai-chat-welcome__prompt">
                Empieza con una de estas ideas:
              </p>
              <div className="ai-chat-suggestions">
                {SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    className="ai-chat-suggestion"
                    onClick={() => handleSuggestionClick(suggestion)}
                    disabled={isLoading}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <ChatMessage key={message.id} message={message} />
              ))}
            </>
          )}
          {error ? (
            <div className="ai-chat-error" role="alert">
              <AlertTriangle size={20} />
              <span>{error}</span>
            </div>
          ) : null}
          <span ref={messagesEndRef} />
        </main>

        <form className="ai-chat-input-container" onSubmit={handleSubmit}>
          <div className="ai-chat-input-wrapper">
            <textarea
              ref={inputRef}
              className="ai-chat-input"
              placeholder="Escribe tu pregunta o pega el contenido que quieras analizar..."
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isLoading}
            />
            <button
              type="submit"
              className={`ai-chat-send-btn ${isLoading ? "ai-chat-send-btn--loading" : ""}`}
              disabled={isLoading || !inputValue.trim()}
            >
              {isLoading ? <Loader2 className="ai-chat-send-btn__spinner" size={20} /> : <Send size={18} />}
            </button>
          </div>
          <p className="ai-chat-input-hint">
            Presiona Enter para enviar · Shift + Enter para agregar un salto de
            línea
          </p>
        </form>

        <footer className="ai-chat-footer">
          <p>
            Respuestas potenciadas por Google Gemini Flash
            {usage?.total_tokens ? (
              <span> · {usage.total_tokens} tokens usados</span>
            ) : null}
          </p>
        </footer>
      </div>
    </div>
  );
};
